import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { io } from 'socket.io-client';
import api from '../api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':4000' : ''));

function AdminChat() {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    // Tell server the owner is online
    socket.on('connect', () => {
      socket.emit('owner_online');
    });

    socket.on('notification', () => loadConversations());
    socket.on('new_message', (msg) => {
      setMessages(prev => [...prev, msg]);
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const { data } = await api.get('/chat/conversations');
      setConversations(data.conversations);
    } catch (err) { console.error(err); }
  };

  const openConversation = async (conv) => {
    setActiveConv(conv);
    socketRef.current?.emit('join_conversation', conv.id);
    try {
      const { data } = await api.get(`/chat/conversations/${conv.id}/messages`);
      setMessages(data.messages);
    } catch (err) { console.error(err); }
  };

  const sendReply = () => {
    if (!input.trim() || !activeConv) return;

    socketRef.current?.emit('send_message', {
      conversation_id: activeConv.id,
      sender_type: 'owner',
      sender_name: 'me',
      content: input.trim()
    });

    setInput('');
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Messages</h1>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '20px', minHeight: '500px' }}>
        {/* Conversation list */}
        <div style={{ borderRight: '1px solid var(--border-soft)', paddingRight: '16px' }}>
          {conversations.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '20px 0' }}>
              no messages yet
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => openConversation(conv)}
                style={{
                  padding: '12px 14px',
                  borderRadius: 'var(--radius-xs)',
                  cursor: 'pointer',
                  marginBottom: '4px',
                  background: activeConv?.id === conv.id ? 'var(--gold-light)' : 'transparent',
                  transition: 'all 0.15s'
                }}
              >
                <div style={{ fontWeight: 500, fontSize: '0.88rem' }}>{conv.visitor_name}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {conv.context_date ? `Day: ${conv.context_date}` : conv.context_type || 'general'}
                  {' \u00B7 '}
                  {format(new Date(conv.last_message_at), 'MMM d, h:mm a')}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Messages */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {!activeConv ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
              select a conversation
            </div>
          ) : (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {messages.map(msg => (
                  <div key={msg.id} style={{
                    maxWidth: '75%',
                    padding: '10px 14px',
                    borderRadius: '14px',
                    fontSize: '0.88rem',
                    lineHeight: '1.5',
                    alignSelf: msg.sender_type === 'owner' ? 'flex-end' : 'flex-start',
                    background: msg.sender_type === 'owner' ? 'var(--ink)' : 'var(--bg-subtle)',
                    color: msg.sender_type === 'owner' ? '#fff' : 'var(--text-body)',
                    borderBottomRightRadius: msg.sender_type === 'owner' ? '4px' : '14px',
                    borderBottomLeftRadius: msg.sender_type === 'visitor' ? '4px' : '14px'
                  }}>
                    {msg.sender_type === 'visitor' && (
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: '2px', fontWeight: 500 }}>
                        {msg.sender_name}
                      </div>
                    )}
                    {msg.content}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div style={{ display: 'flex', gap: '10px', paddingTop: '12px', borderTop: '1px solid var(--border-soft)' }}>
                <input
                  type="text"
                  className="chat-input"
                  placeholder="reply..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); }}
                  style={{ flex: 1 }}
                />
                <button className="btn btn-primary" onClick={sendReply}>Send</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminChat;
